/* eslint-disable no-param-reassign */
import { createAsyncThunk } from '@reduxjs/toolkit';

import getThumbnailUrls from '../../helpers/getThumbnailUrls';
import { setThumbnailUrls, extendAssets } from '../helpers/assets';
import {
  setListingAssets,
  setListingVariantAssets,
  setMasterAssets,
  setMasterVariantAssets,
} from './helpers/entityAdapter';
import { shouldSuppressMediaErrorToast } from './helpers/showChannelProductGoneToast';
import {
  splitAssetsByListingProducts,
  splitAssetsByListingVariants,
  splitAssetsByMasterProducts,
  splitAssetsByMasterVariants,
} from './helpers/splitBulkSearchAssets';

export const SYNC_MEDIA_SEARCH_SIZE = 250;

const mapAssetsWithPermissions = (assets) => assets.map((asset) => ({
  ...asset,
  permissions: Object
    .keys(asset.permissions || {})
    .reduce((negativePermissions, key) => ({ ...negativePermissions, [key]: false }), {}),
}));

const applyPrefetchedAssetsToState = (state, {
  masterAssetsByProductId,
  masterVariantAssetsByVariantId,
  listingAssetsByListingId,
  listingVariantAssetsByVariantId,
  masterProductIds,
  masterVariantEntries,
  listingProductEntries,
  listingVariantEntries,
}) => {
  masterProductIds.forEach((productId) => {
    const assets = masterAssetsByProductId[productId] || [];
    state.items.forEach((product) => {
      if (product._id === productId) {
        product.assets = assets;
      }
    });
    setMasterAssets(state, productId, assets);
  });

  masterVariantEntries.forEach(({ variantId }) => {
    setMasterVariantAssets(
      state,
      variantId,
      masterVariantAssetsByVariantId[variantId] || [],
    );
  });

  listingProductEntries.forEach(({ listingProductId }) => {
    setListingAssets(
      state,
      listingProductId,
      listingAssetsByListingId[listingProductId] || [],
    );
  });

  listingVariantEntries.forEach(({ variantId }) => {
    setListingVariantAssets(
      state,
      variantId,
      listingVariantAssetsByVariantId[variantId] || [],
    );
  });
};

export const prefetchSyncMedia = createAsyncThunk(
  'products/prefetchSyncMedia',
  async (layer = {}, {
    extra: { Logger, Toast, sdk },
    getState,
  }) => {
    const productSelection = layer.productSelection;
    const masterProductIds = layer.masterProductIds || [];
    const masterVariantEntries = layer.masterVariantEntries || [];
    const listingProductEntries = layer.listingProductEntries || [];
    const listingVariantEntries = layer.listingVariantEntries || [];

    try {
      const { team } = getState().user;
      const rootCollectionId = getState().collections?.collections?.my?._id;
      const { data } = await sdk.assets.search({
        tagId: rootCollectionId,
        from: 0,
        size: SYNC_MEDIA_SEARCH_SIZE,
        productSelection,
      });

      const images = data.images?.length ? extendAssets(data.images) : [];
      const thumbnailUrls = images.length
        ? await getThumbnailUrls(images.map(({ _id }) => _id))
        : {};
      const processedAssets = mapAssetsWithPermissions(
        setThumbnailUrls(images, thumbnailUrls, team?.storageType === 's3'),
      );

      return {
        masterAssetsByProductId: splitAssetsByMasterProducts(
          processedAssets,
          masterProductIds,
        ),
        masterVariantAssetsByVariantId: splitAssetsByMasterVariants(
          processedAssets,
          masterVariantEntries,
        ),
        listingAssetsByListingId: splitAssetsByListingProducts(
          processedAssets,
          listingProductEntries,
        ),
        listingVariantAssetsByVariantId: splitAssetsByListingVariants(
          processedAssets,
          listingVariantEntries,
        ),
        masterProductIds,
        masterVariantEntries,
        listingProductEntries,
        listingVariantEntries,
      };
    } catch (error) {
      Logger.log('UI', 'ToastProductsNotLoaded');
      if (!shouldSuppressMediaErrorToast()) {
        Toast(
          'Products not loaded. Refresh and try again',
          { autoClose: false, type: 'error' },
        );
      }
      Logger.error(new Error('Error sync media prefetch'), { error }, [
        'PrefetchSyncMediaFailed',
        { errorMessage: 'Sync media can not be fetched from backend.' },
      ]);
      throw error;
    }
  },
  {
    condition: (layer) => {
      const productSelection = layer?.productSelection;
      return Boolean(
        productSelection
        && (productSelection.productIds?.length || productSelection.variantIds?.length),
      );
    },
  },
);

export const reducer = (builder) => {
  builder.addCase(
    prefetchSyncMedia.fulfilled,
    (state, { payload }) => {
      applyPrefetchedAssetsToState(state, payload);
    },
  );
};
